import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import HRChangePassword from './components/HRChangePassword';
import { isAuthenticatedFor, hrMustChangePassword } from './api/auth';

// Super Admin Pages (lazy-loaded so each route ships its own chunk)
const SAOverview = lazy(() => import('./pages/superadmin/Overview'));
const SACompanies = lazy(() => import('./pages/superadmin/Companies'));
const SAPackages = lazy(() => import('./pages/superadmin/Packages'));
const SAEmployees = lazy(() => import('./pages/superadmin/Employees'));
const SAFinancial = lazy(() => import('./pages/superadmin/Financial'));
const SAInsurance = lazy(() => import('./pages/superadmin/Insurance'));
const SAAnalytics = lazy(() => import('./pages/superadmin/Analytics'));
const SALogs = lazy(() => import('./pages/superadmin/Logs'));

// B2B Client Pages
const B2BOverview = lazy(() => import('./pages/b2bclient/Overview'));
const B2BEmployees = lazy(() => import('./pages/b2bclient/Employees'));
const B2BPackages = lazy(() => import('./pages/b2bclient/Packages'));
const B2BAnalytics = lazy(() => import('./pages/b2bclient/Analytics'));
const B2BBilling = lazy(() => import('./pages/b2bclient/Billing'));

// The dashboard shell (sidebar + navbar + routed page). The active dashboard
// is derived from the URL so the navbar switcher and direct links stay in sync.
function DashboardShell() {
  const location = useLocation();
  const [pwChanged, setPwChanged] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const dashboard = location.pathname.startsWith('/b2b') ? 'b2b' : 'superadmin';

  // Each dashboard is gated by its own role: Admin needs the superuser token,
  // HR needs the CompanyAdmin token. (Employees use the mobile app, not this console.)
  const roleForDashboard = dashboard === 'b2b' ? 'hr' : 'admin';
  if (!isAuthenticatedFor(roleForDashboard)) {
    return <Navigate to={`/login?role=${roleForDashboard}`} replace />;
  }

  // HR must replace the temporary password before reaching the dashboard.
  if (dashboard === 'b2b' && hrMustChangePassword() && !pwChanged) {
    return <HRChangePassword forced onDone={() => setPwChanged(true)} />;
  }

  return (
    <div className={`app-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      <Sidebar dashboard={dashboard} open={sidebarOpen} />
      <div className="main-area">
        <Navbar dashboard={dashboard} onToggleSidebar={() => setSidebarOpen((o) => !o)} sidebarOpen={sidebarOpen} />
        <div className="page-content">
          <Suspense fallback={<div className="page-loading">Loading…</div>}>
            <Routes>
              {/* Super Admin Routes */}
              <Route path="/superadmin" element={<SAOverview />} />
              <Route path="/superadmin/companies" element={<SACompanies />} />
              <Route path="/superadmin/packages" element={<SAPackages />} />
              <Route path="/superadmin/employees" element={<SAEmployees />} />
              <Route path="/superadmin/financial" element={<SAFinancial />} />
              <Route path="/superadmin/insurance" element={<SAInsurance />} />
              <Route path="/superadmin/analytics" element={<SAAnalytics />} />
              <Route path="/superadmin/logs" element={<SALogs />} />

              {/* B2B Client Routes */}
              <Route path="/b2b" element={<B2BOverview />} />
              <Route path="/b2b/employees" element={<B2BEmployees />} />
              <Route path="/b2b/packages" element={<B2BPackages />} />
              <Route path="/b2b/analytics" element={<B2BAnalytics />} />
              <Route path="/b2b/billing" element={<B2BBilling />} />

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/superadmin" replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/*" element={<DashboardShell />} />
      </Routes>
    </Router>
  );
}

export default App;
